import React, { Fragment, useEffect, useState } from 'react'
import { Transition, Menu } from '@headlessui/react'
import { useGlobalState } from '@/state'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'

import classNames from '@/utils/classNamesLocal'
import { ThemeToggle } from '@/shared/ThemeToggle'

import { MenuIcon, SelectorIcon, XIcon } from '@heroicons/react/solid'

function MenuLink(props) {
  const { to, children, ...rest } = props;
  const classes = 'text-clcnavy border-transparent hover:text-clcnavy-light dark:text-clcnavy-light dark:hover:text-clcnavy dark:bg-gray-700 hover:bg-gray-200 group flex items-center border-l-4 px-3 py-2 text-sm font-medium rounded-r-md'
  return <a href={to} className={classes} {...rest}>{children}</a>
}

const MainNavMenuItem = (props) => {
  const { selected, url, name } = props
  return (
      <Menu.Item>
        <a href={url} className={classNames(
          selected ? 'bg-gray-100 text-gray-900 dark:bg-gray-800' : 'text-gray-700 dark:bg-gray-700',
          'block px-4 py-2 text-sm dark:text-white '
        )}
        > {name} <span className="sr-only">Open {name}</span> </a>
      </Menu.Item>
  )
}

export default function Header (props) {
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')
  const [h1, setH1] = useState(props.h1)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    if (!currentUser?.id) { setCurrentUser(props.currentUser) }
  }, [])

  // Sync sidebar open state with html.sidebar-open so the ERB drawer + backdrop + Escape key all stay consistent.
  useEffect(() => {
    const html = document.documentElement
    const sync = () => setSidebarOpen(html.classList.contains('sidebar-open'))
    const obs = new MutationObserver(sync)
    obs.observe(html, { attributes: true, attributeFilter: ['class'] })
    sync()
    return () => obs.disconnect()
  }, [])

  const toggleSidebar = () => {
    document.documentElement.classList.toggle('sidebar-open')
  }

  if (!currentUser?.id) { return null }

  return <span className='border-b-2 border-clcpurple'>
    <nav>
      <div className="relative z-10 flex-shrink-0 flex h-16 bg-white dark:bg-slate-900">
        <div className="flex items-center lg:hidden">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-expanded={sidebarOpen}
            aria-controls="mobile-sidebar"
            className="bg-white dark:bg-slate-900 inline-flex items-center justify-center p-2 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <span className="sr-only">Open main menu</span>
            {sidebarOpen ? (
              <XIcon className="block h-6 w-6" aria-hidden="true" />
            ) : (
              <MenuIcon className="block h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>

            <div className="flex-1 px-1 items-center flex justify-between bg-white dark:bg-slate-900 dark:text-white">
              <h1 className="ml-16 text-2xl relative h-8 w-auto align-middle mb-0">{h1} </h1>
              <div className="w-auto ml-4 flex items-center lg:ml-6 px-3 bg-white dark:bg-slate-900">
                <a href="/courses" className="hidden md:block whitespace-nowrap mr-5 py-2 text-sm">
                  <span>Courses</span>
                </a>
                <a href="/teach_with_us" className="hidden md:block whitespace-nowrap mr-5 py-2 text-sm">
                  <span>Teach With Us</span>
                </a>

                {/* Notifications bell, plain <a> for a full page nav, since the
                    Header lives in its own BrowserRouter tree (see application.jsx). */}
                <a
                  href="/notifications"
                  className="relative inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 mr-2"
                  aria-label={`Notifications${currentUser?.notificationsUnreadCount ? ` (${currentUser.notificationsUnreadCount} unread)` : ''}`}
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" clipRule="evenodd" />
                  </svg>
                  {currentUser?.notificationsUnreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-clcpurple text-white text-[10px] font-bold leading-none">
                      {currentUser.notificationsUnreadCount > 99 ? '99+' : currentUser.notificationsUnreadCount}
                    </span>
                  )}
                </a>

                {/* Profile dropdown */}
                <Menu as="div" className="ml-3 relative md:hidden">
                  {({ open }) => (
                    <>
                      <div>
                        <Menu.Button className="max-w-xs flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 leading-none">
                          <span className="sr-only" hidden>Open user menu</span>
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gray-500 overflow-hidden">
                              {currentUser?.imageUrl ? (
                                <img src={currentUser.imageUrl} alt="" className="block h-full w-full object-cover" />
                              ) : (
                                <span className="leading-none text-white capitalize">{currentUser?.email?.[0]}</span>
                              )}
                            </span>
                          </div>
                        </Menu.Button>
                      </div>
                      <Transition
                        show={open}
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items
                          static
                          className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-white ring-opacity-5 divide-y divide-gray-200 focus:outline-none"
                        >
                          <div>
                            <Menu.Item>
                              <ThemeToggle />
                            </Menu.Item>
                            <Menu.Item as={MenuLink} to='/dashboard'> Dashboard </Menu.Item>
                            <Menu.Item as={MenuLink} to='/profile'> Profile </Menu.Item>
                            <Menu.Item as={MenuLink} to='/settings'> Settings </Menu.Item>
                            <Menu.Item as={MenuLink} to='/subscriptions'> Membership </Menu.Item>
                            <Menu.Item as={MenuLink} to='/chat'> Discord </Menu.Item>
                          </div>
                          <div>
                            <Menu.Item as={MenuLink} to='/changelog'> What's New </Menu.Item>
                            <Menu.Item as={MenuLink} to='/feedback'> Feedback </Menu.Item>
                            <Menu.Item as={MenuLink} to='/faqs'> Support </Menu.Item>
                            <Menu.Item as={MenuLink} to='/logout'> Logout </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </>
                  )}
                </Menu>

                {/* User account dropdown */}
                <Menu as="div" className="relative inline-block text-left hidden md:block mt-6 md:mt-0">
                  {({ open }) => (
                    <>
                      <div>
                        <Menu.Button className="group w-full bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-800 rounded-md px-2 md:px-3.5 py-2 text-sm text-left font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-cccpurple">
                          <span className="flex w-full justify-between items-center">
                            <span className="flex min-w-0 items-center justify-between space-x-3">

                              <div className="flex-shrink-0">
                                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500 overflow-hidden">
                                  {currentUser?.imageUrl ? (
                                    <img src={currentUser.imageUrl} alt="" className="block h-full w-full object-cover" />
                                  ) : (
                                    <span className="leading-none text-white capitalize">{currentUser?.email?.[0]}</span>
                                  )}
                                </span>
                              </div>
                              <span className="flex-1 flex flex-col min-w-0 w-20 md:w-32 overflow-hidden">
                                <span className="text-gray-900 dark:text-white text-sm font-medium truncate">{currentUser?.displayName}</span>
                                <span className="text-xs truncate">{currentUser?.access === 'member' ? <span className='text-clcpurple'>Member</span> : <span className='text-clcpurple'>Free Plan</span>}</span>
                              </span>
                            </span>
                            <SelectorIcon
                              className="flex-shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                              aria-hidden="true"
                            />
                          </span>
                        </Menu.Button>
                      </div>
                      <Transition
                        show={open}
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items
                          static
                          className="z-10 origin-top absolute right-0 left-0 mt-1 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-opacity-5 focus:outline-none"
                        >
                          <div>
                            <Menu.Item>
                              <ThemeToggle />
                            </Menu.Item>

                            <Menu.Item as={MenuLink} to='/dashboard'> Dashboard </Menu.Item>
                            <Menu.Item as={MenuLink} to='/profile'> Profile </Menu.Item>
                            <Menu.Item as={MenuLink} to='/settings'> Settings </Menu.Item>
                            <Menu.Item as={MenuLink} to='/subscriptions'> Membership </Menu.Item>
                            <Menu.Item as={MenuLink} to='/chat'> Discord </Menu.Item>
                          </div>
                          <div>
                            <Menu.Item as={MenuLink} to='/changelog'> What's New </Menu.Item>
                            <Menu.Item as={MenuLink} to='/feedback'> Feedback </Menu.Item>
                            <Menu.Item as={MenuLink} to='/faqs'> Support </Menu.Item>
                            <Menu.Item as={MenuLink} to='/logout'> Logout </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </>
                  )}
                </Menu>
              </div>
            </div>
          </div>
    </nav>
  </span>
}

MainNavMenuItem.propTypes = {
  selected: PropTypes.bool.isRequired,
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
}
